import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, MapPin, Users, DollarSign, ArrowLeft, CreditCard, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

// Get the API URL from the environment or use the default
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3011/api';

const DonationForm = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    amount: '',
    paymentMethod: 'UPI',
    transactionId: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch project details
  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        // Fetch a project from the active projects endpoint and filter by ID
        const response = await axios.get(`${API_URL}/public/projects/active`);
        const foundProject = response.data.find(p => p._id === projectId);
        
        if (foundProject) {
          setProject(foundProject);
        } else {
          setError('Project not found');
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching project:', err);
        setError('Failed to load project details');
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);

  // Fetch bank details for the NGO
  useEffect(() => {
    if (project && project.ngo) {
      const fetchBankDetails = async () => {
        try {
          console.log('Fetching bank details for NGO:', project.ngo);
          const ngoId = typeof project.ngo === 'object' ? project.ngo._id : project.ngo;
          const response = await axios.get(`${API_URL}/bank-details/ngo/${ngoId}`);
          
          if (response.data && response.data.data) {
            console.log('Bank details fetched successfully:', response.data.data);
            setBankDetails(response.data.data);
            // Show bank details section if payment method is Bank Transfer
            if (formData.paymentMethod === 'Bank Transfer') {
              setShowBankDetails(true);
            }
          }
        } catch (err) {
          console.error('Error fetching bank details:', err);
          // Don't show error toast as bank details might not exist yet
        }
      };
      fetchBankDetails();
    }
  }, [project, formData.paymentMethod]);

  // Handle payment screenshot upload
  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPaymentScreenshot(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid donation amount');
      return;
    }

    if (formData.paymentMethod !== 'cash' && !paymentScreenshot) {
      toast.error('Please upload a payment screenshot');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create form data for file upload
      const formDataObj = new FormData();
      formDataObj.append('donorName', formData.name);
      formDataObj.append('donorEmail', formData.email);
      formDataObj.append('donorPhone', formData.phone || '');
      formDataObj.append('amount', formData.amount);
      formDataObj.append('paymentMethod', formData.paymentMethod);
      formDataObj.append('transactionId', formData.transactionId || '');
      formDataObj.append('notes', formData.message || '');
      
      if (paymentScreenshot) {
        formDataObj.append('screenshot', paymentScreenshot);
      }
      
      // Submit payment proof
      console.log('Submitting payment proof to:', `${API_URL}/payment-proofs/project/${projectId}`);
      await axios.post(`${API_URL}/payment-proofs/project/${projectId}`, formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Thank you for your donation! Your payment is pending verification.');
      navigate(`/projects/${projectId}/thank-you`, { 
        state: { 
          donorName: formData.name,
          amount: formData.amount,
          projectTitle: project?.title,
          pending: true
        } 
      });
    } catch (err) {
      console.error('Error processing donation:', err);
      toast.error('Failed to process donation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format date to readable string
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="h-64 bg-gray-200 rounded mb-4"></div>
            <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-red-700">{error || 'Project not found'}</p>
                <p className="mt-2">
                  <Link to="/projects" className="text-red-700 font-medium underline">
                    Return to projects
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-5xl mx-auto">
        <Link to={`/projects/${projectId}`} className="inline-flex items-center text-red-600 hover:text-red-800 mb-6">
          <ArrowLeft size={16} className="mr-2" />
          Back to project
        </Link>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Project Summary */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Donation Summary</h2>
            
            <div className="mb-6">
              <div className="relative h-48 rounded-lg overflow-hidden mb-4">
                <img
                  src={project.image || 'https://via.placeholder.com/800x600?text=Campaign+Image'}
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
              <p className="text-gray-600 mb-4">{project.description}</p>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="flex items-center text-gray-600">
                  <Calendar size={18} className="mr-2 text-red-500" />
                  <span>{formatDate(project.startDate)}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <MapPin size={18} className="mr-2 text-red-500" />
                  <span>{project.location}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <Users size={18} className="mr-2 text-red-500" />
                  <span>{project.beneficiaries}</span>
                </div>
                {project.goalType === 'Fundraising' && (
                  <div className="flex items-center text-gray-600">
                    <DollarSign size={18} className="mr-2 text-red-500" />
                    <span>₹{project.goalAmount.toLocaleString()}</span>
                  </div>
                )}
              </div>
              
              {project.goalType === 'Fundraising' && (
                <div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1">
                    <div 
                      className="bg-red-600 h-2.5 rounded-full" 
                      style={{ width: `${Math.min(100, (project.raisedAmount / project.goalAmount) * 100)}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">₹{project.raisedAmount.toLocaleString()} raised</span>
                    <span className="text-gray-600">{Math.round((project.raisedAmount / project.goalAmount) * 100)}% of ₹{project.goalAmount.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
            
            {project.ngo && (
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-medium text-gray-500 mb-2">Organized by</h4>
                <div className="flex items-center">
                  {project.ngo.logo ? (
                    <img
                      src={project.ngo.logo}
                      alt={project.ngo.name}
                      className="h-10 w-10 rounded-full object-cover mr-3"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                      <User size={20} className="text-gray-500" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">{project.ngo.name}</p>
                    <p className="text-xs text-gray-500">Verified NGO</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Donation Form */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Make a Donation</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500"
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                  Donation Amount (₹) *
                </label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  min="1"
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500"
                  required
                />
                
                <div className="flex space-x-2 mt-2">
                  {[100, 500, 1000, 5000].map(amount => (
                    <button
                      key={amount}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, amount: amount.toString() }))}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      ₹{amount}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div
                    className={`border rounded-md p-3 flex items-center cursor-pointer ${
                      formData.paymentMethod === 'UPI' ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    onClick={() => {
                      setFormData(prev => ({ ...prev, paymentMethod: 'UPI' }));
                      // Show UPI details if available
                      setShowBankDetails(bankDetails && bankDetails.upiId);
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={formData.paymentMethod === 'UPI' ? '#ef4444' : '#9ca3af'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 9l6-6 6 6" />
                      <path d="M6 15l6 6 6-6" />
                    </svg>
                    <span className="ml-2">UPI</span>
                  </div>
                  <div
                    className={`border rounded-md p-3 flex items-center cursor-pointer ${
                      formData.paymentMethod === 'Bank Transfer' ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    onClick={() => {
                      setFormData(prev => ({ ...prev, paymentMethod: 'Bank Transfer' }));
                      // Always show bank details for bank transfer
                      setShowBankDetails(true);
                    }}
                  >
                    <CreditCard size={20} className={formData.paymentMethod === 'Bank Transfer' ? 'text-red-500' : 'text-gray-400'} />
                    <span className="ml-2">Bank Transfer</span>
                  </div>
                </div>
              </div>
              
              {/* Bank Details (shown when Bank Transfer is selected) */}
              {formData.paymentMethod === 'Bank Transfer' && (
                <div className="mb-4 p-4 bg-gray-50 rounded-md border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-900">Bank Transfer Details</h4>
                    {bankDetails && (
                      <button 
                        type="button"
                        className="text-red-600 hover:text-red-800 text-sm flex items-center"
                        onClick={() => {
                          const bankDetailsText = `Account Holder: ${bankDetails.accountHolderName}\nAccount Number: ${bankDetails.accountNumber}\nBank Name: ${bankDetails.bankName}\nBranch: ${bankDetails.branchName}\nIFSC Code: ${bankDetails.ifscCode}\nAccount Type: ${bankDetails.accountType || 'Savings'}`;
                          navigator.clipboard.writeText(bankDetailsText);
                          alert('Bank details copied to clipboard!');
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                        </svg>
                        Copy All
                      </button>
                    )}
                  </div>
                  {bankDetails ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <p><span className="font-medium">Account Holder:</span> {bankDetails.accountHolderName}</p>
                        <button 
                          type="button"
                          className="text-gray-500 hover:text-gray-700 text-xs"
                          onClick={() => {
                            navigator.clipboard.writeText(bankDetails.accountHolderName);
                            alert('Account holder name copied!');
                          }}
                        >
                          Copy
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <p><span className="font-medium">Account Number:</span> {bankDetails.accountNumber}</p>
                        <button 
                          type="button"
                          className="text-gray-500 hover:text-gray-700 text-xs"
                          onClick={() => {
                            navigator.clipboard.writeText(bankDetails.accountNumber);
                            alert('Account number copied!');
                          }}
                        >
                          Copy
                        </button>
                      </div>
                      <p><span className="font-medium">Bank Name:</span> {bankDetails.bankName}</p>
                      <p><span className="font-medium">Branch:</span> {bankDetails.branchName}</p>
                      <div className="flex items-center justify-between">
                        <p><span className="font-medium">IFSC Code:</span> {bankDetails.ifscCode}</p>
                        <button 
                          type="button"
                          className="text-gray-500 hover:text-gray-700 text-xs"
                          onClick={() => {
                            navigator.clipboard.writeText(bankDetails.ifscCode);
                            alert('IFSC code copied!');
                          }}
                        >
                          Copy
                        </button>
                      </div>
                      {bankDetails.accountType && (
                        <p><span className="font-medium">Account Type:</span> {bankDetails.accountType}</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Bank details not available. Please contact the NGO directly.</p>
                  )}
                </div>
              )}
              
              {/* UPI Details (shown when UPI is selected) */}
              {formData.paymentMethod === 'UPI' && bankDetails && bankDetails.upiId && (
                <div className="mb-4 p-4 bg-gray-50 rounded-md border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">UPI Payment Details</h4>
                  <div className="flex items-center justify-between">
                    <p className="text-sm"><span className="font-medium">UPI ID:</span> {bankDetails.upiId}</p>
                    <button 
                      type="button"
                      className="text-red-600 hover:text-red-800 text-sm flex items-center"
                      onClick={() => {
                        navigator.clipboard.writeText(bankDetails.upiId);
                        alert('UPI ID copied to clipboard!');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                      </svg>
                      Copy
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Use this UPI ID to make your payment</p>
                </div>
              )}
              
              {/* Transaction ID */}
              <div className="mb-4">
                <label htmlFor="transactionId" className="block text-sm font-medium text-gray-700 mb-1">
                  Transaction ID/Reference Number
                </label>
                <input
                  type="text"
                  id="transactionId"
                  name="transactionId"
                  value={formData.transactionId}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500"
                />
              </div>
              
              {/* Payment Screenshot Upload */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Screenshot *
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    {screenshotPreview ? (
                      <div className="relative">
                        <img 
                          src={screenshotPreview} 
                          alt="Payment screenshot preview" 
                          className="mx-auto h-32 object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setScreenshotPreview(null);
                            setPaymentScreenshot(null);
                          }}
                          className="absolute top-0 right-0 bg-red-600 text-white rounded-full p-1"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <>
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className="flex text-sm text-gray-600">
                          <label htmlFor="screenshot-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-red-600 hover:text-red-500 focus-within:outline-none">
                            <span>Upload a file</span>
                            <input 
                              id="screenshot-upload" 
                              name="screenshot" 
                              type="file" 
                              className="sr-only" 
                              accept="image/*"
                              onChange={handleScreenshotChange}
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">
                          PNG, JPG, GIF up to 10MB
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Message (Optional)
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="3"
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500"
                ></textarea>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-red-600 text-white py-3 px-4 rounded-md font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-red-300"
              >
                {isSubmitting ? 'Processing...' : `Donate ₹${formData.amount || '0'}`}
              </button>
              
              <p className="text-xs text-gray-500 mt-4 text-center">
                By donating, you agree to our terms and conditions. All donations are secure and encrypted.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonationForm;
